import { useMemo } from "react";

import { Button } from "common/components/ui/Button";
import Checkbox from "common/components/ui/Checkbox";
import {
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  DrawerDescription,
  DrawerFooter,
  DrawerTitle,
} from "common/components/ui/Drawer";
import ProfileImage from "common/components/ui/ProfileImage";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

interface AccountsToCancelProps {
  goBack: () => void;
  goToNextStep: () => void;
  accountsToCancel: string[];
  setAccountsToCancel: (accounts: string[]) => void;
  seatsToCancel: number;
}

export default function AccountsToCancel({
  goBack,
  goToNextStep,
  accountsToCancel,
  setAccountsToCancel,
  seatsToCancel,
}: AccountsToCancelProps) {
  const isDesktop = useTwBreakpoint("lg");

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;

  const { accounts } = useSelectedWorkspaceContext();

  const userAccountsWithLicense = useMemo(
    () => accounts.filter(({ license, type }) => type === "user" && !!license),
    [accounts],
  );

  const isLimitReached = accountsToCancel.length === seatsToCancel;

  return (
    <>
      <Title>Remove seats</Title>
      <Description className="mb-8">
        You will not have enough outreach seats for your currently active
        accounts. Choose which accounts will have their seats removed
      </Description>

      <p className="mb-4 text-black-500">
        Selected:{" "}
        <span className="text-sm-bold text-black-950">
          {accountsToCancel.length}/{seatsToCancel}
        </span>
      </p>

      <section className="flex flex-col gap-y-3 overflow-y-auto scrollbar-thin pb-4 lg:max-h-[min(378px,60dvh)]">
        {userAccountsWithLicense.map(
          ({
            id,
            full_name: fullName,
            picture_url: pictureUrl,
            occupation,
          }) => {
            const isSelected = !!accountsToCancel.find(
              (accountId) => accountId === id,
            );

            const isDisabled = isLimitReached && !isSelected;

            function handleClick() {
              setAccountsToCancel(
                isSelected
                  ? accountsToCancel.filter((accountId) => accountId !== id)
                  : [...accountsToCancel, id],
              );
            }

            return (
              <button
                type="button"
                key={id}
                className="group flex items-center gap-x-2 rounded-3xl border border-black-200 bg-white p-3 disabled:cursor-not-allowed disabled:bg-black-200"
                onClick={handleClick}
                disabled={isDisabled}
              >
                <ProfileImage src={pictureUrl} />
                <div className="flex flex-1 flex-col gap-y-1">
                  <h5 className="line-clamp-1 text-left text-sm-bold group-disabled:text-black-400">
                    {fullName}
                  </h5>
                  <span className="line-clamp-1  text-left text-xs-medium text-black-500 group-disabled:text-black-400">
                    {occupation}
                  </span>
                </div>
                <Checkbox
                  checked={isSelected}
                  disabled={isDisabled}
                  isStyleOnly
                />
              </button>
            );
          },
        )}
      </section>

      <Footer>
        <Button variant="secondary-black" onClick={goBack}>
          Back
        </Button>
        <Button onClick={goToNextStep} disabled={!isLimitReached}>
          Next
        </Button>
      </Footer>
    </>
  );
}
